
export const volumeFacts = {
  liter: {
    name: "Liter",
    shortDescription: "The primary metric unit of volume",
    history: "The liter emerged during the French Revolution's radical standardization of measurements. Its brilliance lay in its relationship to other units - one liter was defined as the volume of one kilogram of pure water at its maximum density. This interconnection between mass, volume, and length units was revolutionary, creating a cohesive system where each unit reinforced the others.",
    origin: "Created as part of the metric system during the French Revolution, though its current definition wasn't standardized until 1964",
    modernUsage: "The liter's compatibility with the decimal system made it ideal for modern commerce and science, though it wasn't officially added to the SI system until 1964",
    funFacts: [
      "Early metric system designers considered making the liter exactly equal to one cubic decimeter, but natural variations in water density complicated this relationship",
      "The spelling debate between 'litre' and 'liter' lasted until 1964, when the CGPM finally standardized it to 'litre' (though 'liter' remains acceptable in US English)",
      "A liter of water at 4°C weighs almost exactly one kilogram - a relationship that helped establish both units' credibility"
    ],
    related: ["milliliter", "cubic meter", "gallon"],
    images: []
  },
  milliliter: {
    name: "Milliliter",
    shortDescription: "One-thousandth of a liter, equal to one cubic centimeter",
    history: "The milliliter, exactly equal to one cubic centimeter, represents the metric system's elegant integration of volume and length measurements. Originally called a 'cubic centimeter' (cc), the term 'milliliter' became preferred to emphasize its volume rather than geometric nature.",
    origin: "Derived from the liter as part of the metric system",
    modernUsage: "Widely used in laboratory work and medicine",
    funFacts: [
      "Exactly equal to one cubic centimeter (cc)",
      "Modern syringes show both ml and cc markings",
      "Preferred over 'cc' to emphasize volume measurement"
    ],
    related: ["liter", "cubic meter", "fluid ounce"],
    images: []
  },
  "fluid ounce": {
    name: "Fluid Ounce",
    shortDescription: "A volume unit distinct from the weight-based ounce",
    history: "The fluid ounce, distinct from its weight-based namesake, exemplifies the complexity of volume measurement history. The U.S. fluid ounce (1/16 of a U.S. pint) differs from the Imperial fluid ounce (1/20 of an Imperial pint), reflecting their separate evolutionary paths.",
    origin: "Developed separately in British and American systems",
    modernUsage: "Commonly used in the US for beverages and cooking",
    funFacts: [
      "U.S. and Imperial fluid ounces have different volumes",
      "Early measures were calibrated differently for alcohol and water",
      "Represents different fractions of a pint in US and UK systems"
    ],
    related: ["pint", "milliliter", "gallon"],
    images: []
  },
  gallon: {
    name: "Gallon",
    shortDescription: "A large unit of volume with different standards in the US and UK",
    history: "The gallon's complicated history spawned multiple definitions. The wine gallon, ale gallon, and corn gallon coexisted in Britain until the imperial gallon (based on 10 pounds of water) unified liquid measurement in 1824. The U.S. retained the smaller Queen Anne wine gallon (231 cubic inches).",
    origin: "Evolved from multiple historical standards",
    modernUsage: "Used for fuel and large liquid volumes, with different US and UK standards",
    funFacts: [
      "Imperial gallon based on 10 pounds of water",
      "U.S. kept the smaller wine gallon standard",
      "Bronze standards were kept in town halls"
    ],
    related: ["quart", "pint", "liter"],
    images: []
  },
  "cubic meter": {
    name: "Cubic Meter",
    shortDescription: "The metric system's standard unit of volume",
    history: "The cubic meter represents the metric system's logical extension of length to volume. Unlike traditional volume units based on trade goods, the cubic meter derives purely from length measurement. Its large size made it particularly useful for civil engineering and bulk material measurement.",
    origin: "Derived directly from the meter in the metric system",
    modernUsage: "Used in construction, engineering, and industrial applications",
    funFacts: [
      "Equal to 1000 liters",
      "Derives purely from length measurement",
      "Essential in modern construction calculations"
    ],
    related: ["liter", "milliliter"],
    images: []
  },
  quart: {
    name: "Quart",
    shortDescription: "A quarter of a gallon, with varying standards",
    history: "The quart, as its name suggests, represented a quarter of a gallon. Like the gallon, it varied between liquid and dry measures and between British and American standards. The measuring devices evolved from simple pottery or wooden vessels to precision-stamped metal containers.",
    origin: "Name derives from being a quarter of a gallon",
    modernUsage: "Still used in US for liquid measurement",
    funFacts: [
      "Name literally means 'quarter' of a gallon",
      "Common for milk sales historically",
      "Different standards for liquid and dry measures"
    ],
    related: ["gallon", "pint"],
    images: []
  },
  pint: {
    name: "Pint",
    shortDescription: "A unit of volume equal to half a quart",
    history: "The pint's etymology traces to the painted marks on ale vessels, though this origin remains debated. The unit varied significantly - the Scotch pint was more than three times the English pint. Beer and milk pints were often different sizes.",
    origin: "Possibly from painted marks on ale vessels",
    modernUsage: "Common in UK and US for beer and milk",
    funFacts: [
      "Scotch pint was triple the English pint",
      "'A pint's a pound' only true for water in US",
      "British pub pints require crown stamp certification"
    ],
    related: ["quart", "fluid ounce"],
    images: []
  },
  bushel: {
    name: "Bushel",
    shortDescription: "A measure of volume derived from the Roman 'bussellus', standardized as eight gallons",
    history: "The bushel's evolution reflects agriculture's transition from volume to weight measurement. Originally a volume measure derived from the Roman 'bussellus' (little box), the bushel became standardized as eight gallons in the Winchester Standards of the 13th century. This 'Winchester bushel,' measuring 2150.42 cubic inches, became the standard throughout the English-speaking world.",
    origin: "From Roman 'bussellus' meaning 'little box'",
    modernUsage: "Still used in U.S. grain markets, with crop yields quoted in bushels per acre",
    funFacts: [
      "Different commodities had different bushel weights",
      "Wheat standardized at 60 pounds per bushel",
      "Market inspectors used bronze measures with leveling bars"
    ],
    related: ["peck", "gallon", "pottle"],
    images: []
  },
  peck: {
    name: "Peck",
    shortDescription: "A quarter bushel (two imperial gallons), used in medieval commerce",
    history: "The peck, equal to a quarter bushel (two imperial gallons), exemplifies how medieval measurements cascaded into smaller, practical units. The word's etymology remains disputed - some link it to the French 'picotin,' a measure for horse feed, while others connect it to the action of 'pecking' at grain.",
    origin: "Disputed etymology, possibly from French 'picotin'",
    modernUsage: "Sometimes still referenced in British preserving recipes",
    funFacts: [
      "Featured in the Peter Piper tongue twister",
      "Measures required official verification marks",
      "Size matched what one person could carry"
    ],
    related: ["bushel", "gallon", "pottle"],
    images: []
  },
  pottle: {
    name: "Pottle",
    shortDescription: "Half a gallon measure, particularly important in medieval retail",
    history: "The pottle, equaling half a gallon (four pints), served as a crucial retail measure in medieval and early modern England. Its name derives from the French 'potel,' meaning a small pot or measure. The pottle pot, typically crafted from wood, pewter, or pottery, became a standard vessel in taverns and markets.",
    origin: "From French 'potel' meaning small pot or measure",
    modernUsage: "Primarily of historical interest",
    funFacts: [
      "Traditional measure for strawberries in London markets",
      "Pottle baskets were tall and narrow for displaying fruit",
      "Standard vessel in medieval taverns"
    ],
    related: ["pint", "gallon", "peck"],
    images: []
  },
  "gill": {
    name: "Gill",
    shortDescription: "A unit of liquid measurement equal to a quarter of a pint",
    history: "The gill originated in England as a standardized measure for wine and spirits. It equals one-quarter of a pint, though its exact volume differs between the imperial system (142 ml) and the US system (118 ml). The term comes from Medieval Latin 'gillo' or 'gello', meaning a wine vessel.",
    origin: "From Medieval Latin 'gillo' or 'gello', meaning a wine vessel",
    modernUsage: "Rarely used today, except in some traditional British recipes and historical contexts",
    funFacts: [
      "Different volumes in US and UK systems",
      "Pronounced 'jill' in UK English",
      "Was a common measure for spirits in taverns"
    ],
    related: ["pint", "fluid ounce"],
    images: []
  },
  "hogshead": {
    name: "Hogshead",
    shortDescription: "A large cask or barrel size used primarily for alcoholic beverages",
    history: "The hogshead emerged in the Late Middle Ages as a standard size for wine casks. Originally equaling 63 gallons, it varied by contents: 54 gallons for beer and 48 gallons for ale in Britain. The name possibly derives from the Old English 'hogsheafod', though its exact etymology remains disputed.",
    origin: "Possibly from Old English 'hogsheafod', though etymology is disputed",
    modernUsage: "Still used occasionally in the wine and spirits industry, particularly for aging certain types of alcohol",
    funFacts: [
      "Different volumes for different liquids",
      "Standard size for tobacco shipping historically",
      "Key unit in colonial American trade"
    ],
    related: ["barrel", "gallon"],
    images: []
  },
  "firkin": {
    name: "Firkin",
    shortDescription: "A small barrel equivalent to a quarter of a barrel or half a kilderkin",
    history: "The firkin was established in the 15th century as a standard measure for ale and beer. The word comes from the Middle Dutch 'vierdekijn', meaning 'fourth' (of a barrel). It traditionally held 9 imperial gallons of beer or 8 imperial gallons of ale.",
    origin: "From Middle Dutch 'vierdekijn', meaning 'fourth'",
    modernUsage: "Still used occasionally in British brewing and some traditional pubs",
    funFacts: [
      "Different volumes for ale and beer",
      "Standard size for butter historically",
      "Name literally means 'little fourth'"
    ],
    related: ["barrel", "hogshead"],
    images: []
  },
  "butt": {
    name: "Butt",
    shortDescription: "A large cask size, traditionally used for wine and sherry",
    history: "The butt was a traditional measure for wine, particularly associated with Spanish and Portuguese wine trade. It equaled about 126 gallons (573 liters) in the wine trade. The term comes from Medieval Latin 'buttis', meaning cask or wineskin.",
    origin: "From Medieval Latin 'buttis', meaning cask or wineskin",
    modernUsage: "Still used in the sherry industry and some traditional wine making",
    funFacts: [
      "Standard measure for sherry casks",
      "Twice the size of a hogshead",
      "Important in medieval wine trade"
    ],
    related: ["hogshead", "barrel"],
    images: []
  }
};
